import {
  useMutation,
  useQuery,
  useQueryClient,
  type MutationObserverOptions,
} from '@tanstack/vue-query';
import type { AxiosError } from 'axios';
import type { ControlBookmark } from '@/types/models/control';
import { mergeMutationOptions, mergeQueryOptions, type QueryOptions } from '@/modules/query/utils';
import { useFramework } from '@/modules/framework/composables';
import api from '../api';
import type { ScopingAnswer } from '../../../types/models/scopingAnswer';
import { useStore } from '../../../store/useStore';
import { CONTROL_BOOKMARKS_QUERY, SCOPING_ANSWERS_QUERY } from './queryKeys';

export function useControlBookmarks(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [CONTROL_BOOKMARKS_QUERY],
      async queryFn() {
        const res = await api.getControlBookmarks();
        return res.data;
      },
    }),
  );

  const controlBookmarksByControlID = computed(() => {
    return Object.fromEntries(
      query.data.value?.map((bookmark) => [bookmark.controlID, bookmark]) ?? [],
    );
  });

  const isControlBookmarked = (controlID: string) => {
    return controlBookmarksByControlID.value?.[controlID]?.bookmarked;
  };

  return {
    ...query,
    controlBookmarksByControlID,
    isControlBookmarked,
  };
}

export function useBookmarkControl(
  options?: MutationObserverOptions<ControlBookmark, AxiosError, string, null>,
) {
  const queryClient = useQueryClient();
  const mutation = useMutation(
    mergeMutationOptions(options, {
      async mutationFn(controlID: string) {
        const res = await api.bookmarkControl(controlID);
        return res.data;
      },
      onSuccess: (bookmark, controlID) => {
        queryClient.setQueryData<ControlBookmark[]>([CONTROL_BOOKMARKS_QUERY], (oldData) => {
          if (!oldData) return [bookmark];
          const newData = oldData.filter((b) => b.controlID !== controlID);
          // Remove the existing bookmark from the new data if it already exists
          newData.filter((b) => b.controlID !== controlID);
          // Add the new bookmark to the new data
          return [...newData, bookmark];
        });
        queryClient.invalidateQueries({ queryKey: [CONTROL_BOOKMARKS_QUERY] });
      },
    }),
  );
  return mutation;
}

export function useUnbookmarkControl(
  options?: MutationObserverOptions<ControlBookmark, AxiosError, string, null>,
) {
  const queryClient = useQueryClient();
  const mutation = useMutation(
    mergeMutationOptions(options, {
      async mutationFn(controlID: string) {
        const res = await api.unbookmarkControl(controlID);
        return res.data;
      },
      onSuccess: (_, controlID) => {
        queryClient.setQueryData<ControlBookmark[]>([CONTROL_BOOKMARKS_QUERY], (oldData) => {
          if (!oldData) return [];
          return oldData.filter((b) => b.controlID !== controlID);
        });
        queryClient.invalidateQueries({ queryKey: [CONTROL_BOOKMARKS_QUERY] });
      },
    }),
  );
  return mutation;
}

export function useScopingAnswers(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [SCOPING_ANSWERS_QUERY],
      async queryFn() {
        const res = await api.getScopingAnswers();
        return res.data;
      },
    }),
  );
  const scopingAnswers = computed(() => query.data.value ?? {});
  // temporary sync with store until we have removed the assessment store (COR-809)
  const store = useStore();
  watchEffect(() => {
    if (store) {
      store.commit('assessment/SET_SCOPING_ANSWERS', scopingAnswers.value);
    }
  });

  const { sortedDomains, sortedControls } = useFramework();
  const totalScopingQuestions = computed(() => {
    return sortedDomains.value.filter((domain) => domain.scopingQuestion).length;
  });
  const totalScopingAnswered = computed(
    () => sortedDomains.value.filter((domain) => !!scopingAnswers.value[domain.domainID]).length,
  );

  const outOfScopeDomains = computed(() => {
    return sortedDomains.value.filter(
      (domain) => domain.scopingQuestion && scopingAnswers.value[domain.domainID]?.answer === false,
    );
  });

  const outOfScopeControlIDs = computed(() =>
    sortedControls.value
      .filter((control) =>
        outOfScopeDomains.value.some((domain) => domain.domainID === control.domainID),
      )
      .map((control) => control.controlID),
  );

  const isDomainOutOfScope = (domainID: string) => {
    return outOfScopeDomains.value.some((domain) => domain.domainID === domainID);
  };

  const isControlOutOfScope = (controlID: string) => {
    return outOfScopeControlIDs.value.includes(controlID);
  };

  return {
    ...query,
    query,
    totalScopingQuestions,
    totalScopingAnswered,
    scopingAnswers,
    outOfScopeDomains,
    isDomainOutOfScope,
    isControlOutOfScope,
  };
}

type ChangeScopingAnswerPayload = Pick<ScopingAnswer, 'domainID' | 'answer' | 'notes'>;

export function useChangeScopingAnswer(
  options?: MutationObserverOptions<ScopingAnswer, AxiosError, ChangeScopingAnswerPayload, null>,
) {
  const queryClient = useQueryClient();
  const mutation = useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ domainID, answer, notes }: ChangeScopingAnswerPayload) {
        const res = await api.changeScopingAnswer(domainID, answer, notes);
        return res.data;
      },
      onSuccess: (scopingAnswer) => {
        queryClient.setQueryData<Record<string, ScopingAnswer>>(
          [SCOPING_ANSWERS_QUERY],
          (oldData) => {
            if (!oldData) return { [scopingAnswer.domainID]: scopingAnswer };
            return { ...oldData, [scopingAnswer.domainID]: scopingAnswer };
          },
        );
        queryClient.invalidateQueries({ queryKey: [SCOPING_ANSWERS_QUERY] });
      },
    }),
  );

  return mutation;
}
